import "@xyflow/react/dist/style.css";
import "./print.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useStoreApi,
  type Connection,
  type EdgeTypes,
  type NodeChange,
  type NodeTypes,
  type OnConnect,
  type OnEdgesChange,
  type OnNodesChange,
} from "@xyflow/react";
import {
  type CableId,
  deviceId as mkDeviceId,
  type DeviceId,
  type SheetId,
} from "@electro-planner/domain";
import { listTerminals } from "@electro-planner/domain";
import type { TerminalKind } from "@electro-planner/domain";
import { DEFAULT_SHEET_ID, GRID_SIZE } from "./constants.ts";
import { CommandPalette, type CommandPaletteCommand } from "./components/CommandPalette.tsx";
import { DeviceInspector, type InspectorDevice } from "./components/DeviceInspector.tsx";
import { DocumentMetaDialog } from "./components/DocumentMetaDialog.tsx";
import { EdgeInspector, type InspectorEdge } from "./components/EdgeInspector.tsx";
import { DocumentSwitcher } from "./components/DocumentSwitcher.tsx";
import { EditorToolbar } from "./components/EditorToolbar.tsx";
import { JunctionsLayer } from "./components/JunctionsLayer.tsx";
import { ReleaseNotesDialog } from "./components/ReleaseNotesDialog.tsx";
import { SheetTabs } from "./components/SheetTabs.tsx";
import { SymbolPalette, SYMBOL_DRAG_MIME } from "./components/SymbolPalette.tsx";
import { TitleBlock } from "./components/TitleBlock.tsx";
import { LATEST_RELEASE, RELEASE_STORAGE_KEY } from "./releases.ts";
import { OrthogonalWireEdge } from "./edges/OrthogonalWireEdge.tsx";
import { ElectricalSymbolNode } from "./nodes/ElectricalSymbolNode.tsx";
import { useDeviceIndex } from "./hooks/useDeviceIndex.ts";
import { useDomainMutations, type ClipboardPayload } from "./hooks/useDomainMutations.ts";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts.ts";
import {
  computeAlignment,
  computeDistribution,
  type AlignMode,
  type DistributeMode,
} from "./align.ts";
import { downloadBackup, encodeDocToBackup, restoreDocFromBackup } from "./export/backup.ts";
import { RevisionsDialog } from "./revisions/RevisionsDialog.tsx";
import { buildBomCsv, buildConductorCsv, downloadCsv } from "./export/csv.ts";
import { touchDocument } from "../lib/documentsRegistry.ts";
import { useProjectMeta } from "./hooks/useProjectMeta.ts";
import { useReactFlowProjection } from "./hooks/useReactFlowProjection.ts";
import { useSheetNavigation } from "./hooks/useSheetNavigation.ts";
import { useUndoManager } from "./hooks/useUndoManager.ts";
import { useYjsDoc } from "./hooks/useYjsDoc.ts";
import { useAutosaveStatus } from "./hooks/useAutosaveStatus.ts";
import { createIsValidConnection } from "./validation/connectionRules.ts";

const NODE_TYPES: NodeTypes = {
  electricalSymbol: ElectricalSymbolNode,
};

const EDGE_TYPES: EdgeTypes = {
  orthogonalWire: OrthogonalWireEdge,
};

const DEFAULT_EDGE_OPTIONS = {
  type: "orthogonalWire" as const,
};

type Props = {
  documentId: string;
  onSwitchDocument?: (id: string) => void;
};

function CanvasInner({ documentId, onSwitchDocument }: Props) {
  const { doc, maps, synced } = useYjsDoc(documentId, {
    id: DEFAULT_SHEET_ID,
    title: "Ark 1",
  });

  const {
    sheets,
    activeSheetId,
    setActiveSheetId,
    activeIndex,
  } = useSheetNavigation(doc, maps);
  const activeSheetTitle = sheets[activeIndex]?.title ?? "Ark";
  const { nodes, edges, junctions } = useReactFlowProjection(doc, maps, activeSheetId);
  const autosave = useAutosaveStatus(doc);
  const deviceIndex = useDeviceIndex(doc, maps);
  const mutations = useDomainMutations(doc, maps, activeSheetId);
  const undoState = useUndoManager(maps);
  const rf = useReactFlow();
  const storeApi = useStoreApi();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const clipboardRef = useRef<ClipboardPayload | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const [metaDialogOpen, setMetaDialogOpen] = useState(false);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const { meta: projectMeta, setField: setProjectMetaField } = useProjectMeta(doc, maps);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<string[]>([]);

  // Håll dokumentregistret synkat: varje gång dokumentet öppnas eller
  // projektnamnet ändras uppdaterar vi post i registret så att dokumentlistan
  // visar rätt titel + sortering efter senast ändrad.
  useEffect(() => {
    if (!synced) return;
    touchDocument(documentId, { title: projectMeta.projectName ?? "" });
  }, [documentId, synced, projectMeta.projectName]);

  // Auto-open releasenoter när versionen har stigit sedan förra besöket.
  // SSR-säker: useEffect körs bara i browsern, så window/localStorage är alltid
  // definierade här. Skriv senast sedda version när dialogen stängs, inte när
  // den öppnas — annars missar användaren att "nytt" vid en flimrande sida.
  useEffect(() => {
    try {
      const last = window.localStorage.getItem(RELEASE_STORAGE_KEY);
      if (last !== LATEST_RELEASE.version) setReleaseNotesOpen(true);
    } catch {
      // localStorage kan vara avstängt (private mode, policy) — då öppnar vi
      // inte automatiskt, användaren kan fortfarande trycka på knappen.
    }
  }, []);

  const closeReleaseNotes = useCallback((open: boolean) => {
    setReleaseNotesOpen(open);
    if (!open) {
      try {
        window.localStorage.setItem(RELEASE_STORAGE_KEY, LATEST_RELEASE.version);
      } catch {
        // ignorera — samma skäl som ovan
      }
    }
  }, []);

  const handlePrint = useCallback(() => {
    // Fit först så hela ritningen hamnar på pappret, sen rendera en frame
    // innan dialogen öppnas — annars ser browsern den gamla viewporten.
    rf.fitView({ padding: 0.1, duration: 0 });
    window.setTimeout(() => window.print(), 120);
  }, [rf]);

  const exportBomCsv = useCallback(() => {
    const csv = buildBomCsv(maps);
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(`apparatlista-${documentId}-${date}.csv`, csv);
  }, [maps, documentId]);

  const exportConductorCsv = useCallback(() => {
    const csv = buildConductorCsv(maps);
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(`tradlista-${documentId}-${date}.csv`, csv);
  }, [maps, documentId]);

  const exportBackup = useCallback(() => {
    downloadBackup(doc, documentId);
  }, [doc, documentId]);

  const backupFileInputRef = useRef<HTMLInputElement | null>(null);
  const importBackup = useCallback(() => {
    backupFileInputRef.current?.click();
  }, []);

  const handleBackupFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Nolla input-value:n direkt så att samma fil kan väljas igen om importen
      // avbryts (browsern triggar inte onChange om value är identisk).
      e.target.value = "";
      if (!file) return;
      const ok = window.confirm(
        "Öppna backup-fil?\n\nDin aktuella ritning ersätts. Du kan ångra (Ctrl+Z) direkt efter.",
      );
      if (!ok) return;
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        restoreDocFromBackup(doc, maps, bytes);
      } catch (err) {
        console.error("Kunde inte läsa backup-fil:", err);
        window.alert(
          "Backup-filen kunde inte läsas. Är det verkligen en .ep-fil från electro-planner?",
        );
      }
    },
    [doc, maps],
  );

  const onSelectionChange = useCallback(
    ({
      nodes: selNodes,
      edges: selEdges,
    }: {
      nodes: { id: string }[];
      edges: { id: string }[];
    }) => {
      setSelectedDeviceIds(selNodes.map((n) => n.id));
      setSelectedEdgeIds(selEdges.map((e) => e.id));
    },
    [],
  );

  const rotateSelected = useCallback(() => {
    if (selectedDeviceIds.length === 0) return;
    for (const id of selectedDeviceIds) {
      mutations.rotateDevice(id as DeviceId, 90);
    }
  }, [selectedDeviceIds, mutations]);

  const copySelected = useCallback(() => {
    if (selectedDeviceIds.length === 0) return;
    clipboardRef.current = mutations.buildClipboardPayload(selectedDeviceIds);
  }, [selectedDeviceIds, mutations]);

  const paste = useCallback(() => {
    const payload = clipboardRef.current;
    if (!payload || payload.devices.length === 0) return;
    const newIds = mutations.pasteClipboard(payload, {
      x: GRID_SIZE * 2,
      y: GRID_SIZE * 2,
    });
    // Flytta markeringen till de nya devicesarna så användaren ser vad som hände.
    const { addSelectedNodes } = storeApi.getState();
    addSelectedNodes(newIds);
  }, [mutations, storeApi]);

  const duplicateSelected = useCallback(() => {
    if (selectedDeviceIds.length === 0) return;
    const payload = mutations.buildClipboardPayload(selectedDeviceIds);
    const newIds = mutations.pasteClipboard(payload, {
      x: GRID_SIZE * 2,
      y: GRID_SIZE * 2,
    });
    const { addSelectedNodes } = storeApi.getState();
    addSelectedNodes(newIds);
  }, [selectedDeviceIds, mutations, storeApi]);

  const selectAll = useCallback(() => {
    const { addSelectedNodes } = storeApi.getState();
    addSelectedNodes(nodes.map((n) => n.id));
  }, [storeApi, nodes]);

  const alignSelected = useCallback(
    (mode: AlignMode) => {
      if (selectedDeviceIds.length < 2) return;
      const targets = selectedDeviceIds
        .map((id) => {
          const n = nodes.find((x) => x.id === id);
          return n ? { id: n.id, x: n.position.x, y: n.position.y } : null;
        })
        .filter((t): t is { id: string; x: number; y: number } => t != null);
      const next = computeAlignment(targets, mode);
      for (const p of next) {
        mutations.moveDevice(p.id as DeviceId, { x: p.x, y: p.y });
      }
    },
    [selectedDeviceIds, nodes, mutations],
  );

  const distributeSelected = useCallback(
    (mode: DistributeMode) => {
      if (selectedDeviceIds.length < 3) return;
      const targets = selectedDeviceIds
        .map((id) => {
          const n = nodes.find((x) => x.id === id);
          return n ? { id: n.id, x: n.position.x, y: n.position.y } : null;
        })
        .filter((t): t is { id: string; x: number; y: number } => t != null);
      const next = computeDistribution(targets, mode);
      for (const p of next) {
        mutations.moveDevice(p.id as DeviceId, { x: p.x, y: p.y });
      }
    },
    [selectedDeviceIds, nodes, mutations],
  );

  const groupSelectedEdgesAsCable = useCallback(() => {
    if (selectedEdgeIds.length === 0) return;
    mutations.groupConductorsAsCable(selectedEdgeIds);
  }, [selectedEdgeIds, mutations]);

  const ungroupSelectedEdges = useCallback(() => {
    if (selectedEdgeIds.length === 0) return;
    for (const id of selectedEdgeIds) mutations.leaveCable(id);
  }, [selectedEdgeIds, mutations]);

  // Pilknapps-nudge: ett rutsteg per tryck (5 med Shift) för att flytta
  // markerade symboler exakt längs rutnätet — precisionsjustering utan drag.
  const nudgeSelected = useCallback(
    (gx: number, gy: number) => {
      if (selectedDeviceIds.length === 0) return;
      mutations.nudgeDevices(
        selectedDeviceIds.map((id) => id as DeviceId),
        gx * GRID_SIZE,
        gy * GRID_SIZE,
      );
    },
    [selectedDeviceIds, mutations],
  );

  // Ctrl+Shift+F: zooma in på markerade symboler. Utan markering faller det
  // tillbaka till vanlig fitView så genvägen alltid gör något användbart.
  const zoomToSelection = useCallback(() => {
    if (selectedDeviceIds.length === 0) {
      rf.fitView({ padding: 0.1, duration: 200 });
      return;
    }
    rf.fitView({
      padding: 0.3,
      duration: 200,
      nodes: selectedDeviceIds.map((id) => ({ id })),
    });
  }, [rf, selectedDeviceIds]);

  const addSheet = useCallback(() => {
    const id = mutations.addSheet();
    setActiveSheetId(id);
  }, [mutations, setActiveSheetId]);

  const deleteActiveSheet = useCallback(() => {
    mutations.deleteSheet(activeSheetId);
  }, [mutations, activeSheetId]);

  const duplicateActiveSheet = useCallback(() => {
    const newId = mutations.duplicateSheet(activeSheetId);
    if (newId) setActiveSheetId(newId);
  }, [mutations, activeSheetId, setActiveSheetId]);

  const duplicateSpecificSheet = useCallback(
    (id: SheetId) => {
      const newId = mutations.duplicateSheet(id);
      if (newId) setActiveSheetId(newId);
    },
    [mutations, setActiveSheetId],
  );

  const goToSheet = useCallback(
    (delta: number) => {
      if (sheets.length === 0) return;
      const next = (activeIndex + delta + sheets.length) % sheets.length;
      const target = sheets[next];
      if (target) setActiveSheetId(target.id);
    },
    [sheets, activeIndex, setActiveSheetId],
  );

  const nextSheet = useCallback(() => goToSheet(1), [goToSheet]);
  const prevSheet = useCallback(() => goToSheet(-1), [goToSheet]);

  const inspectorDevice = useMemo<InspectorDevice | null>(() => {
    if (selectedDeviceIds.length !== 1) return null;
    const n = nodes.find((x) => x.id === selectedDeviceIds[0]);
    if (!n) return null;
    const d = n.data as {
      symbolId: string;
      designation: string;
      rotation: 0 | 90 | 180 | 270;
      symbol: {
        id: string;
        label: { sv: string; en: string };
        designationPrefix: string;
        familyKey: string;
      };
    };
    return {
      id: n.id,
      designation: d.designation,
      rotation: d.rotation,
      symbol: {
        id: d.symbol.id,
        label: d.symbol.label,
        designationPrefix: d.symbol.designationPrefix,
        familyKey: d.symbol.familyKey,
      },
      position: { x: n.position.x, y: n.position.y },
    };
  }, [selectedDeviceIds, nodes]);

  // Edge-inspector visas bara när exakt en tråd (och inga devices) är markerad.
  // Samtidig multi-selection ger tom view — låt device-inspector vinna om båda
  // finns, eftersom devices är det vanligaste redigeringsmålet.
  const inspectorEdge = useMemo<InspectorEdge | null>(() => {
    if (selectedDeviceIds.length > 0) return null;
    if (selectedEdgeIds.length !== 1) return null;
    const e = edges.find((x) => x.id === selectedEdgeIds[0]);
    if (!e) return null;
    return {
      id: e.id,
      area: e.data?.area,
      color: e.data?.color,
      circuitLabel: e.data?.circuitLabel,
      cableDesignation: e.data?.cableDesignation,
      cableId: e.data?.cableId,
      cableType: e.data?.cableType,
      source: e.source,
      target: e.target,
    };
  }, [selectedEdgeIds, selectedDeviceIds, edges]);

  const isDesignationDuplicate = useMemo(() => {
    if (!inspectorDevice) return false;
    return nodes.some(
      (n) =>
        n.id !== inspectorDevice.id &&
        (n.data as { designation?: string }).designation === inspectorDevice.designation,
    );
  }, [inspectorDevice, nodes]);

  const insertSymbolAtCenter = useCallback(
    (symbolOrId: string | import("@electro-planner/symbols").SymbolDefinition) => {
      const w = wrapperRef.current?.clientWidth ?? 800;
      const h = wrapperRef.current?.clientHeight ?? 600;
      const center = rf.screenToFlowPosition({ x: w / 2, y: h / 2 });
      const x = Math.round(center.x / GRID_SIZE) * GRID_SIZE;
      const y = Math.round(center.y / GRID_SIZE) * GRID_SIZE;
      mutations.addDeviceFromSymbol(symbolOrId, { x, y });
    },
    [rf, mutations],
  );

  const commands = useMemo<CommandPaletteCommand[]>(
    () => [
      {
        id: "undo",
        label: "Ångra",
        shortcut: "Ctrl+Z",
        keywords: ["undo", "angra"],
        run: undoState.undo,
      },
      {
        id: "redo",
        label: "Gör om",
        shortcut: "Ctrl+Y",
        keywords: ["redo", "gor om"],
        run: undoState.redo,
      },
      {
        id: "export-pdf",
        label: "Exportera PDF",
        shortcut: "Ctrl+P",
        keywords: ["pdf", "print", "skriv ut"],
        run: handlePrint,
      },
      {
        id: "export-bom",
        label: "Exportera apparatlista (CSV)",
        keywords: ["csv", "apparat", "lista", "bom", "bill"],
        run: exportBomCsv,
      },
      {
        id: "export-conductors",
        label: "Exportera trådlista (CSV)",
        keywords: ["csv", "trad", "tråd", "lista", "wire"],
        run: exportConductorCsv,
      },
      {
        id: "fit-view",
        label: "Passa in ritningen",
        keywords: ["zoom", "fit"],
        run: () => rf.fitView({ padding: 0.1, duration: 200 }),
      },
      {
        id: "zoom-to-selection",
        label: "Zooma till markering",
        shortcut: "Ctrl+Shift+F",
        keywords: ["zoom", "selection", "markering", "fokus"],
        run: zoomToSelection,
      },
      {
        id: "export-backup",
        label: "Ladda ner backup-fil (.ep)",
        keywords: ["backup", "fil", "spara", "download", "export"],
        run: exportBackup,
      },
      {
        id: "import-backup",
        label: "Öppna backup-fil (.ep)…",
        keywords: ["backup", "öppna", "import", "restore", "fil"],
        run: importBackup,
      },
      {
        id: "open-revisions",
        label: "Revisioner (spara/återställ)…",
        keywords: ["revision", "snapshot", "historik", "återställ", "spara"],
        run: () => setRevisionsOpen(true),
      },
      {
        id: "rotate-selected",
        label: "Rotera markering 90°",
        shortcut: "R",
        keywords: ["rotera", "rotate", "vrid"],
        run: rotateSelected,
      },
      {
        id: "copy",
        label: "Kopiera markering",
        shortcut: "Ctrl+C",
        keywords: ["copy", "kopiera", "klipp"],
        run: copySelected,
      },
      {
        id: "paste",
        label: "Klistra in",
        shortcut: "Ctrl+V",
        keywords: ["paste", "klistra", "infoga"],
        run: paste,
      },
      {
        id: "duplicate",
        label: "Duplicera markering",
        shortcut: "Ctrl+D",
        keywords: ["duplicate", "duplicera", "kopia"],
        run: duplicateSelected,
      },
      {
        id: "select-all",
        label: "Markera allt",
        shortcut: "Ctrl+A",
        keywords: ["select", "markera", "allt", "alla"],
        run: selectAll,
      },
      {
        id: "add-sheet",
        label: "Nytt ark",
        keywords: ["nytt", "ark", "sheet", "lägg till"],
        run: addSheet,
      },
      {
        id: "duplicate-sheet",
        label: "Duplicera aktuellt ark",
        keywords: ["duplicera", "kopiera", "clone", "ark", "sheet"],
        run: duplicateActiveSheet,
      },
      {
        id: "delete-sheet",
        label: "Radera aktuellt ark",
        keywords: ["radera", "ark", "sheet", "ta bort"],
        run: deleteActiveSheet,
      },
      {
        id: "next-sheet",
        label: "Nästa ark",
        shortcut: "Ctrl+PgDn",
        keywords: ["next", "nästa", "ark", "sheet"],
        run: nextSheet,
      },
      {
        id: "prev-sheet",
        label: "Föregående ark",
        shortcut: "Ctrl+PgUp",
        keywords: ["forra", "föregående", "previous", "ark"],
        run: prevSheet,
      },
      {
        id: "align-left",
        label: "Justera vänster",
        shortcut: "Ctrl+Shift+L",
        keywords: ["align", "justera", "vänster", "left"],
        run: () => alignSelected("left"),
      },
      {
        id: "align-right",
        label: "Justera höger",
        shortcut: "Ctrl+Shift+R",
        keywords: ["align", "justera", "höger", "right"],
        run: () => alignSelected("right"),
      },
      {
        id: "align-top",
        label: "Justera topp",
        shortcut: "Ctrl+Shift+T",
        keywords: ["align", "justera", "topp", "top", "över"],
        run: () => alignSelected("top"),
      },
      {
        id: "align-bottom",
        label: "Justera botten",
        shortcut: "Ctrl+Shift+B",
        keywords: ["align", "justera", "botten", "bottom", "under"],
        run: () => alignSelected("bottom"),
      },
      {
        id: "align-center-x",
        label: "Justera centrum (vertikal linje)",
        keywords: ["align", "justera", "center", "centrum", "mitt"],
        run: () => alignSelected("centerX"),
      },
      {
        id: "align-center-y",
        label: "Justera centrum (horisontell linje)",
        keywords: ["align", "justera", "center", "centrum", "mitt"],
        run: () => alignSelected("centerY"),
      },
      {
        id: "distribute-horizontal",
        label: "Fördela horisontellt",
        keywords: ["distribute", "fördela", "fordela", "horisontell"],
        run: () => distributeSelected("horizontal"),
      },
      {
        id: "distribute-vertical",
        label: "Fördela vertikalt",
        keywords: ["distribute", "fördela", "fordela", "vertikal"],
        run: () => distributeSelected("vertical"),
      },
      {
        id: "group-cable",
        label: "Gruppera markerade trådar som kabel",
        keywords: ["kabel", "cable", "gruppera", "w1", "bunta", "samla"],
        run: groupSelectedEdgesAsCable,
      },
      {
        id: "ungroup-cable",
        label: "Bryt ut markerade trådar ur kabel",
        keywords: ["kabel", "cable", "bryt", "ut", "lossa", "ungroup"],
        run: ungroupSelectedEdges,
      },
      {
        id: "document-meta",
        label: "Ritningsinställningar",
        keywords: ["projekt", "uppdrag", "beställare", "title", "block", "konstruktor"],
        run: () => setMetaDialogOpen(true),
      },
      {
        id: "release-notes",
        label: "Visa nyheter (releasenoter)",
        keywords: ["nyheter", "release", "changelog", "versioner"],
        run: () => setReleaseNotesOpen(true),
      },
      // Dynamiska "Gå till"-kommandon per device. keywords innehåller
      // både beteckning och "gå till X" så fuzzy-sök i cmdk hittar dem på
      // båda skriv-sätt. Endast devices med primarySheet visas (orphans — utan
      // layout — kan inte navigeras till).
      ...deviceIndex
        .filter((d) => d.primarySheet && typeof d.x === "number" && typeof d.y === "number")
        .map<CommandPaletteCommand>((d) => ({
          id: `goto-${d.deviceId}`,
          label: `Gå till ${d.designation}`,
          keywords: [
            "gå till",
            "ga till",
            "goto",
            "find",
            "hitta",
            d.designation,
            d.designation.toLowerCase(),
          ],
          run: () => {
            if (d.primarySheet && d.primarySheet !== activeSheetId) {
              setActiveSheetId(d.primarySheet);
            }
            // rAF så sheet-bytets re-render hinner innan vi zoomar in.
            requestAnimationFrame(() => {
              if (typeof d.x === "number" && typeof d.y === "number") {
                rf.setCenter(d.x, d.y, { zoom: 1.5, duration: 250 });
              }
            });
          },
        })),
    ],
    [
      undoState.undo,
      undoState.redo,
      handlePrint,
      exportBomCsv,
      exportConductorCsv,
      exportBackup,
      importBackup,
      rf,
      rotateSelected,
      copySelected,
      paste,
      duplicateSelected,
      selectAll,
      addSheet,
      duplicateActiveSheet,
      deleteActiveSheet,
      nextSheet,
      prevSheet,
      alignSelected,
      distributeSelected,
      groupSelectedEdgesAsCable,
      ungroupSelectedEdges,
      zoomToSelection,
      deviceIndex,
      activeSheetId,
      setActiveSheetId,
    ],
  );

  useKeyboardShortcuts({
    onUndo: undoState.undo,
    onRedo: undoState.redo,
    onPrint: handlePrint,
    onOpenCommandPalette: () => setPaletteOpen((v) => !v),
    onRotateSelected: rotateSelected,
    onCopy: copySelected,
    onPaste: paste,
    onDuplicate: duplicateSelected,
    onSelectAll: selectAll,
    onNextSheet: nextSheet,
    onPrevSheet: prevSheet,
    onAlignLeft: () => alignSelected("left"),
    onAlignRight: () => alignSelected("right"),
    onAlignTop: () => alignSelected("top"),
    onAlignBottom: () => alignSelected("bottom"),
    onZoomIn: () => rf.zoomIn({ duration: 120 }),
    onZoomOut: () => rf.zoomOut({ duration: 120 }),
    onZoomFit: () => rf.fitView({ padding: 0.1, duration: 200 }),
    onZoomToSelection: zoomToSelection,
    onNudgeSelected: nudgeSelected,
  });

  const onNodesChange = useCallback<OnNodesChange>(
    (changes) => {
      for (const c of changes) applyNodeChange(c, mutations);
    },
    [mutations],
  );

  const onEdgesChange = useCallback<OnEdgesChange>(
    (changes) => {
      for (const c of changes) {
        if (c.type === "remove") mutations.deleteConductor(c.id);
      }
    },
    [mutations],
  );

  const onConnect = useCallback<OnConnect>(
    (c: Connection) => {
      if (!c.source || !c.target || !c.sourceHandle || !c.targetHandle) return;
      mutations.connect({
        sourceDeviceId: c.source,
        sourceHandleId: c.sourceHandle,
        targetDeviceId: c.target,
        targetHandleId: c.targetHandle,
      });
    },
    [mutations],
  );

  const isValidConnection = useMemo(
    () =>
      createIsValidConnection((nodeId, handleId) => {
        const terminals = listTerminals(maps);
        const t = terminals.find(
          (x) => x.deviceId === nodeId && x.symbolTerminalId === handleId,
        );
        if (!t) return null;
        return { kind: t.kind as TerminalKind, label: t.label };
      }),
    [maps],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(SYMBOL_DRAG_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const symbolId = e.dataTransfer.getData(SYMBOL_DRAG_MIME);
      if (!symbolId) return;
      const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      // Snap till grid.
      const x = Math.round(pos.x / GRID_SIZE) * GRID_SIZE;
      const y = Math.round(pos.y / GRID_SIZE) * GRID_SIZE;
      mutations.addDeviceFromSymbol(symbolId, { x, y });
    },
    [rf, mutations],
  );

  return (
    <div className="flex h-full min-h-0 w-full">
      <div className="no-print contents">
        <SymbolPalette onSymbolPick={insertSymbolAtCenter} />
      </div>
      <div
        ref={wrapperRef}
        className="relative flex flex-1 flex-col"
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <EditorToolbar
          canUndo={undoState.canUndo}
          canRedo={undoState.canRedo}
          onUndo={undoState.undo}
          onRedo={undoState.redo}
          onExportPdf={handlePrint}
          onExportBom={exportBomCsv}
          onExportConductors={exportConductorCsv}
          onOpenMeta={() => setMetaDialogOpen(true)}
          onOpenCommandPalette={() => setPaletteOpen(true)}
          onOpenReleaseNotes={() => setReleaseNotesOpen(true)}
          autosave={autosave}
          documentSwitcher={
            onSwitchDocument ? (
              <DocumentSwitcher
                currentId={documentId}
                currentTitle={projectMeta.projectName ?? ""}
                onSwitch={onSwitchDocument}
              />
            ) : undefined
          }
        />
        <div className="relative min-h-0 flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onSelectionChange={onSelectionChange}
            isValidConnection={isValidConnection}
            snapToGrid
            snapGrid={[GRID_SIZE, GRID_SIZE]}
            nodeOrigin={[0.5, 0.5]}
            onlyRenderVisibleElements
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={GRID_SIZE} size={1} />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable />
            <JunctionsLayer junctions={junctions} />
          </ReactFlow>
          {!synced && (
            <div className="no-print pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/60 text-sm text-[var(--color-muted)]">
              Laddar lokal ritning…
            </div>
          )}
          {synced && nodes.length === 0 ? (
            <div className="no-print pointer-events-none absolute inset-0 z-0 flex items-center justify-center text-center">
              <div className="rounded-lg border border-dashed border-[var(--color-border)] bg-white/70 px-6 py-5 text-sm text-[var(--color-muted)] shadow-sm">
                <p className="mb-1 font-semibold text-[var(--color-fg)]">
                  Tomt ark — dra en symbol hit
                </p>
                <p>
                  Från paletten till vänster, eller tryck{" "}
                  <kbd className="rounded border border-[var(--color-border)] bg-white px-1 font-mono text-xs">
                    Ctrl+K
                  </kbd>{" "}
                  och sök efter en symbol.
                </p>
              </div>
            </div>
          ) : null}
          <TitleBlock
            documentName={`${activeSheetTitle} (doc: ${documentId})`}
            drawingNumber={`${activeIndex + 1}/${sheets.length}`}
            date={new Date().toISOString().slice(0, 10)}
            projectName={projectMeta.projectName}
            projectNumber={projectMeta.projectNumber}
            clientName={projectMeta.clientName}
            designer={projectMeta.designer}
            companyName={projectMeta.companyName}
            revision={projectMeta.revision}
          />
        </div>
        <SheetTabs
          sheets={sheets}
          activeSheetId={activeSheetId}
          onSelect={setActiveSheetId}
          onAdd={addSheet}
          onRename={mutations.renameSheet}
          onDelete={mutations.deleteSheet}
          onMove={mutations.moveSheet}
          onDuplicate={duplicateSpecificSheet}
        />
      </div>
      {inspectorEdge ? (
        <EdgeInspector
          edge={inspectorEdge}
          onSetArea={(id, area) => mutations.setConductorAttributes(id, { area })}
          onSetColor={(id, color) => mutations.setConductorAttributes(id, { color })}
          onSetCircuitLabel={(id, circuitLabel) =>
            mutations.setConductorAttributes(id, { circuitLabel })
          }
          onCreateCable={(id) => mutations.groupConductorsAsCable([id])}
          onLeaveCable={(id) => mutations.leaveCable(id)}
          onRenameCable={(_id, designation) => {
            if (!inspectorEdge?.cableId) return;
            mutations.renameCable(inspectorEdge.cableId as CableId, designation);
          }}
          onSetCableType={(_id, type) => {
            if (!inspectorEdge?.cableId) return;
            mutations.setCableType(inspectorEdge.cableId as CableId, type);
          }}
          onDelete={(id) => mutations.deleteConductor(id)}
        />
      ) : (
        <DeviceInspector
          device={inspectorDevice}
          isDesignationDuplicate={isDesignationDuplicate}
          onRename={(id, designation) => mutations.renameDevice(id as DeviceId, designation)}
          onSetRotation={(id, rotation) =>
            mutations.setDeviceRotation(id as DeviceId, rotation)
          }
          onDelete={(id) => mutations.deleteDevice(id as DeviceId)}
        />
      )}
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        commands={commands}
        onInsertSymbol={insertSymbolAtCenter}
      />
      <ReleaseNotesDialog open={releaseNotesOpen} onOpenChange={closeReleaseNotes} />
      <RevisionsDialog
        open={revisionsOpen}
        onOpenChange={setRevisionsOpen}
        documentId={documentId}
        getCurrentUpdateBytes={() => encodeDocToBackup(doc)}
        onRestore={(bytes) => restoreDocFromBackup(doc, maps, bytes)}
      />
      <DocumentMetaDialog
        open={metaDialogOpen}
        onOpenChange={setMetaDialogOpen}
        meta={projectMeta}
        onSetField={setProjectMetaField}
      />
      <input
        ref={backupFileInputRef}
        type="file"
        accept=".ep,application/octet-stream"
        className="hidden"
        onChange={handleBackupFileSelected}
      />
    </div>
  );
}

function applyNodeChange(
  change: NodeChange,
  mutations: ReturnType<typeof useDomainMutations>,
): void {
  if (change.type === "position" && change.position && change.dragging === false) {
    const id = change.id as DeviceId;
    mutations.moveDevice(id, change.position);
    return;
  }
  if (change.type === "remove") {
    mutations.deleteDevice(mkDeviceId(change.id));
    return;
  }
}

export function Canvas(props: Props) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}

export type { Props as CanvasProps };
